import { useState } from 'react'
import { sendArtifactsToProjectSight } from '@/integrations/trimble/trimbleApi'
import {
  useActivityService,
  ActivityCategory,
  ActivitySubCategory,
  ActivityItemType,
} from '@dc-shared-web/hooks/api/activity'

export type ProjectSightModalState = 'form' | 'loading' | 'success' | 'error'

interface ProjectSightModalStateData {
  errorMessage?: string
}

interface ExportOptions {
  portfolio: string
  project: string
  contract: string
  selectedChecklists?: Array<{
    id: string
    include?: boolean
  }>
  selectedPlaybooks?: Array<{
    id: string
    include?: boolean
  }>
  documentOptions?: Record<string, boolean>
}

interface UseProjectSightExportProps {
  crunchUUID?: string
  documentName?: string
  dcrunchToken?: string
}

/**
 * Custom hook for handling ProjectSight export functionality
 * Manages export state, submission, and activity tracking
 */
export const useProjectSightExport = ({
  crunchUUID,
  documentName,
  dcrunchToken,
}: UseProjectSightExportProps) => {
  const [modalState, setModalState] = useState<ProjectSightModalState>('form')
  const [modalStateData, setModalStateData] =
    useState<ProjectSightModalStateData>({})
  const { trackActivity } = useActivityService()

  // Process and submit export to ProjectSight
  const submitExport = async (values: ExportOptions) => {
    setModalState('loading')

    // Process checklists with options
    const checklists = (values.selectedChecklists || [])
      .filter((c) => c.include)
      .map((c) => {
        const options = values.documentOptions || {}

        return {
          uuid: c.id,
          include: true,
          include_option1: options[`checklist_${c.id}_option1`] || true,
          include_option2: options[`checklist_${c.id}_option2`] || true,
          include_option3: options[`checklist_${c.id}_option3`] || true,
          include_gen_ai_summary:
            options[`checklist_${c.id}_gen_ai_summary`] || true,
          include_notes: options[`checklist_${c.id}_notes`] || true,
          include_subquestions:
            options[`checklist_${c.id}_subquestions`] || true,
          include_descriptions:
            options[`checklist_${c.id}_descriptions`] || true,
          include_no_answer: options[`checklist_${c.id}_no_answer`] || true,
        }
      })

    // Process playbooks
    const playbooks = (values.selectedPlaybooks || [])
      .filter((p) => p.include)
      .map((p) => ({
        uuid: p.id,
        include: !!p.include,
      }))

    try {
      // Check if at least one checklist or playbook is selected
      const hasSelectedChecklists =
        values.selectedChecklists?.some((c) => c.include) || false
      const hasSelectedPlaybooks =
        values.selectedPlaybooks?.some((p) => p.include) || false

      if (!hasSelectedChecklists && !hasSelectedPlaybooks) {
        throw new Error(
          'Please select at least one checklist or playbook to export'
        )
      }

      // Send artifacts to ProjectSight with the provided options
      await sendArtifactsToProjectSight({
        portfolioId: values.portfolio,
        projectId: values.project,
        contractId: values.contract,
        crunchUUID: crunchUUID || '',
        dcrunchToken: dcrunchToken as string,
        downloadOptions: {
          checklists,
          playbooks,
        },
      })

      // Track successful export
      trackActivity(ActivityCategory.CRUNCH, ActivitySubCategory.DOWNLOADED, {
        itemGuid: crunchUUID || '',
        itemType: ActivityItemType.CRUNCH,
        success: true,
        data: {
          download_type: 'projectsight',
          document_name: documentName || '',
          checklist_ids: checklists.map((c) => c.uuid).join(','),
          playbook_ids: playbooks.map((p) => p.uuid).join(','),
          event: 'export_successful',
          portfolio_id: values.portfolio,
          project_id: values.project,
          contract_id: values.contract,
        },
      })

      setModalState('success')
    } catch (error) {
      console.error('Error sending to ProjectSight:', error)

      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred'

      // Track failed export
      trackActivity(ActivityCategory.CRUNCH, ActivitySubCategory.DOWNLOADED, {
        itemGuid: crunchUUID || '',
        itemType: ActivityItemType.CRUNCH,
        success: false,
        data: {
          download_type: 'projectsight',
          document_name: documentName || '',
          error_message: errorMessage,
          checklist_ids: checklists.map((c) => c.uuid).join(','),
          playbook_ids: playbooks.map((p) => p.uuid).join(','),
          event: 'export_failed',
        },
      })

      setModalStateData({
        errorMessage: errorMessage,
      })
      setModalState('error')
    }
  }

  // Reset modal state
  const resetModalState = () => {
    setModalState('form')
    setModalStateData({})
  }

  return {
    modalState,
    modalStateData,
    submitExport,
    resetModalState,
  }
}

export default useProjectSightExport
